/**
 * Client-side WebRTC diagnostics for Twilio Video calls.
 *
 * Attaches listeners to a Twilio Video Room + all remote/local participants,
 * polls getStats() periodically for byte/packet counters, records which ICE
 * candidate types the browser could gather and which pair was selected, and
 * posts everything to /api/cb/videoCallDiagnostics when the call ends.
 *
 * No PHI is captured — only Twilio track/participant SIDs, event kinds,
 * numeric stats, and browser info. The backend endpoint is unauth so the
 * patient (unauth link) side can post too.
 *
 * Usage:
 *   const diag = installVideoCallDiagnostics(room, {
 *     roomName, role: "STAFF" | "PATIENT", facilityId, userId,
 *   });
 *   // ... later, on any teardown path:
 *   diag.flush();
 *
 * The helper self-flushes on room.disconnected and on window pagehide, so
 * calling flush() manually is optional but harmless (idempotent).
 */

import { devLog } from "../utils/devLog";
import { http } from "../utils/http";
import FeaturesService from "./featuresService";

const DIAGNOSTICS_PATH = "/api/cb/videoCallDiagnostics";

// Resolve the full URL against the same backend baseURL axios uses elsewhere.
// In dev the frontend runs on :9002 and the backend on :8080 — a relative
// fetch would hit the dev server and 404. In prod the axios baseURL is the
// deployed API host.
function diagnosticsUrl() {
  const baseURL =
    (http && http.axios && http.axios.defaults && http.axios.defaults.baseURL) ||
    "";
  return baseURL + DIAGNOSTICS_PATH;
}
const STATS_POLL_INTERVAL_MS = 5000;
const MAX_EVENTS = 500; // guardrail — never balloon memory or POST size

/**
 * Which ICE candidate types the browser managed to gather, and which pair was
 * actually selected.
 *
 * This is the single most diagnostic fact in a "signalling fine, media dead"
 * failure: if only `host` candidates appear, the network blocked STUN/TURN and
 * no media path could ever form. Twilio's own Room.getStats() does not expose
 * candidate data, so we read the native RTCPeerConnection via SDK internals.
 *
 * That path is private API (twilio-video 2.35.0) and may move on upgrade —
 * every step is guarded and any failure yields null rather than disturbing the
 * call. Diagnostics must never be able to break a consultation.
 *
 * Captures candidate TYPE and the ICE server `url` only — never candidate
 * addresses, so no participant network identity is recorded.
 */
async function collectIceInfo(room) {
  try {
    const manager =
      room && room._signaling && room._signaling._peerConnectionManager;
    const pcMap = manager && manager._peerConnections;
    if (!pcMap || typeof pcMap.forEach !== "function") return null;

    const nativePcs = [];
    pcMap.forEach((pcv2) => {
      const native = pcv2 && pcv2._peerConnection;
      if (native && typeof native.getStats === "function") {
        nativePcs.push(native);
      }
    });
    if (!nativePcs.length) return null;

    const perConnection = [];
    for (let i = 0; i < nativePcs.length; i++) {
      const report = await nativePcs[i].getStats();
      const localById = new Map();
      const remoteById = new Map();
      const pairs = [];
      let selectedPairId = null;

      report.forEach((stat) => {
        if (stat.type === "local-candidate") localById.set(stat.id, stat);
        else if (stat.type === "remote-candidate") remoteById.set(stat.id, stat);
        else if (stat.type === "candidate-pair") pairs.push(stat);
        else if (stat.type === "transport" && stat.selectedCandidatePairId) {
          selectedPairId = stat.selectedCandidatePairId;
        }
      });

      // Counts keyed by type, e.g. { host: 2, srflx: 1, "relay/tls": 2 }.
      // No relay key at all => TURN was unreachable.
      const gathered = {};
      const serverUrls = [];
      localById.forEach((c) => {
        const key =
          String(c.candidateType) + (c.relayProtocol ? "/" + c.relayProtocol : "");
        gathered[key] = (gathered[key] || 0) + 1;
        if (c.url && serverUrls.indexOf(c.url) < 0) serverUrls.push(c.url);
      });

      const selectedPair =
        pairs.filter((pr) => pr.id === selectedPairId)[0] ||
        pairs.filter((pr) => pr.nominated && pr.state === "succeeded")[0] ||
        pairs.filter((pr) => pr.state === "succeeded")[0] ||
        null;

      // Short keys on purpose: the backend logs each event through a 512-char
      // cap, and this is the payload we least want truncated.
      //   ice = iceConnectionState, g = gathered types, relay = any relay
      //   gathered, urls = ICE servers reached, sel = selected pair
      //   (st state, lt/rt local+remote type, lp protocol, lrp relay
      //   protocol, tx/rx bytes)
      let sel = null;
      if (selectedPair) {
        const lc = localById.get(selectedPair.localCandidateId);
        const rc = remoteById.get(selectedPair.remoteCandidateId);
        sel = {
          st: selectedPair.state,
          lt: lc && lc.candidateType,
          lp: lc && lc.protocol,
          lrp: lc && lc.relayProtocol,
          rt: rc && rc.candidateType,
          tx: selectedPair.bytesSent,
          rx: selectedPair.bytesReceived,
        };
      }

      perConnection.push({
        ice: nativePcs[i].iceConnectionState,
        g: gathered,
        relay: Object.keys(gathered).some((k) => k.indexOf("relay") === 0),
        urls: serverUrls,
        sel,
      });
    }
    return perConnection;
  } catch (err) {
    return null;
  }
}

/**
 * Post a diagnostics record for a call that never produced a Twilio Room.
 *
 * installVideoCallDiagnostics() can only attach once Video.connect() resolves,
 * so every pre-Room failure — no jwt from the join endpoint, or connect()
 * rejecting because ICE never completed — used to report nothing at all. Those
 * are exactly the failures worth diagnosing: a call that works needs no
 * telemetry.
 *
 * Runs a short standalone ICE probe so the report answers the only question
 * that matters on a locked-down network: could the browser reach STUN/TURN?
 * No candidate beyond `host` means it could not, and no media path could ever
 * have formed.
 *
 * Fire-and-forget: never throws, never blocks the caller's error handling.
 */
export async function reportVideoCallFailure(meta, error, stage) {
  try {
    if (!FeaturesService.isVideoCallDiagnosticLoggingEnabled()) return;
    const startedAt = Date.now();
    const events = [
      {
        ts: 0,
        kind: "callFailed",
        data: {
          stage: stage || "unknown",
          message: error && (error.message || String(error)),
          code: error && (error.code || error.status),
          name: error && error.name,
        },
      },
    ];

    const probe = await probeIceReachability();
    events.push({ ts: Date.now() - startedAt, kind: "iceProbe", data: probe });

    const payload = {
      roomName: meta && meta.roomName,
      role: meta && meta.role,
      userId: meta && meta.userId,
      facilityId: meta && meta.facilityId,
      userAgent:
        typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
      startedAt,
      endedAt: Date.now(),
      durationMs: Date.now() - startedAt,
      events,
    };
    devLog("[VIDEOCALL-DIAG] reporting failure", { stage, probe });
    fetch(diagnosticsUrl(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      keepalive: true,
      body: JSON.stringify(payload),
    }).catch((err) => devLog("[VIDEOCALL-DIAG] failure POST failed", err));
  } catch (err) {
    devLog("[VIDEOCALL-DIAG] reportVideoCallFailure threw", err);
  }
}

/**
 * Gather ICE candidates against Twilio's public STUN for a few seconds and
 * report which types appeared. Credential-free, so it works without a Twilio
 * token — which is the point, since the token stage may be what failed.
 *
 * Only `host` => outbound STUN/UDP is blocked. `srflx` => UDP reaches the
 * internet. This is the single most diagnostic fact when a call will not form.
 */
async function probeIceReachability() {
  const PROBE_TIMEOUT_MS = 5000;
  const RTCPC =
    typeof window !== "undefined" &&
    (window.RTCPeerConnection || window.webkitRTCPeerConnection);
  if (!RTCPC) return { supported: false };

  let pc = null;
  try {
    pc = new RTCPC({
      iceServers: [{ urls: "stun:global.stun.twilio.com:3478" }],
    });
    const gathered = {};
    pc.createDataChannel("probe");

    const done = new Promise((resolve) => {
      const finish = () => resolve();
      const timer = setTimeout(finish, PROBE_TIMEOUT_MS);
      pc.onicecandidate = (e) => {
        if (!e.candidate) {
          clearTimeout(timer);
          finish();
          return;
        }
        const t = e.candidate.type || "unknown";
        gathered[t] = (gathered[t] || 0) + 1;
      };
    });

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await done;

    return {
      gathered,
      reachedStun: Object.keys(gathered).some((k) => k !== "host"),
      gatheringState: pc.iceGatheringState,
    };
  } catch (err) {
    return { error: err && err.message };
  } finally {
    try { if (pc) pc.close(); } catch (_) {}
  }
}

export function installVideoCallDiagnostics(room, meta) {
  devLog("[VIDEOCALL-DIAG] install called", {
    hasRoom: !!room,
    roomName: room && room.name,
    meta,
  });
  if (!room) return { flush: () => {} };

  // Facility opt-in. Gated here rather than at each call site so a future
  // caller cannot forget it. Fails open when features are unknown (the
  // anonymous patient side never has them) — the backend is the authoritative
  // gate and stores nothing unless the facility enabled the flag.
  if (!FeaturesService.isVideoCallDiagnosticLoggingEnabled()) {
    devLog("[VIDEOCALL-DIAG] disabled for this facility - not collecting");
    return { flush: () => {} };
  }

  const startedAt = Date.now();
  const events = [];
  let flushed = false;
  const remoteParticipantHandlers = new Map();
  let lastIceSignature = null;

  const now = () => Date.now() - startedAt;

  const push = (kind, data) => {
    if (events.length >= MAX_EVENTS) return;
    events.push({ ts: now(), kind, data: data || {} });
  };

  push("callStart", {
    roomName: room.name,
    roomSid: room.sid,
    localIdentity: room.localParticipant && room.localParticipant.identity,
    participants: room.participants ? room.participants.size : 0,
  });

  const wireParticipant = (participant) => {
    push("participantConnected", {
      identity: participant.identity,
      sid: participant.sid,
      state: participant.state,
    });

    const onTrackSubscribed = (track) => {
      push("trackSubscribed", {
        identity: participant.identity,
        kind: track.kind,
        sid: track.sid,
        name: track.name,
        isEnabled: track.isEnabled,
      });
    };
    const onTrackUnsubscribed = (track) => {
      push("trackUnsubscribed", {
        identity: participant.identity,
        kind: track.kind,
        sid: track.sid,
      });
    };
    const onTrackStarted = (track) => {
      push("trackStarted", {
        identity: participant.identity,
        kind: track.kind,
        sid: track.sid,
      });
    };
    const onTrackDisabled = (pub) => {
      push("trackDisabled", {
        identity: participant.identity,
        kind: pub.kind,
        trackSid: pub.trackSid,
      });
    };
    const onTrackEnabled = (pub) => {
      push("trackEnabled", {
        identity: participant.identity,
        kind: pub.kind,
        trackSid: pub.trackSid,
      });
    };
    const onTrackSwitchedOff = (track) => {
      push("trackSwitchedOff", {
        identity: participant.identity,
        kind: track.kind,
        sid: track.sid,
      });
    };
    const onTrackSwitchedOn = (track) => {
      push("trackSwitchedOn", {
        identity: participant.identity,
        kind: track.kind,
        sid: track.sid,
      });
    };
    const onReconnecting = () => {
      push("participantReconnecting", { identity: participant.identity });
    };
    const onReconnected = () => {
      push("participantReconnected", { identity: participant.identity });
    };

    participant.on("trackSubscribed", onTrackSubscribed);
    participant.on("trackUnsubscribed", onTrackUnsubscribed);
    participant.on("trackStarted", onTrackStarted);
    participant.on("trackDisabled", onTrackDisabled);
    participant.on("trackEnabled", onTrackEnabled);
    participant.on("trackSwitchedOff", onTrackSwitchedOff);
    participant.on("trackSwitchedOn", onTrackSwitchedOn);
    participant.on("reconnecting", onReconnecting);
    participant.on("reconnected", onReconnected);

    remoteParticipantHandlers.set(participant.sid, {
      participant,
      handlers: {
        trackSubscribed: onTrackSubscribed,
        trackUnsubscribed: onTrackUnsubscribed,
        trackStarted: onTrackStarted,
        trackDisabled: onTrackDisabled,
        trackEnabled: onTrackEnabled,
        trackSwitchedOff: onTrackSwitchedOff,
        trackSwitchedOn: onTrackSwitchedOn,
        reconnecting: onReconnecting,
        reconnected: onReconnected,
      },
    });
  };

  // Wire already-present participants
  room.participants.forEach(wireParticipant);

  // Room-level events
  const onParticipantConnected = (p) => wireParticipant(p);
  const onParticipantDisconnected = (p) => {
    push("participantDisconnected", {
      identity: p.identity,
      sid: p.sid,
    });
  };
  const onReconnecting = (err) => {
    push("roomReconnecting", {
      code: err && err.code,
      message: err && err.message,
    });
  };
  const onReconnected = () => push("roomReconnected", {});
  const onDominantSpeakerChanged = (p) => {
    push("dominantSpeakerChanged", {
      identity: p && p.identity,
    });
  };
  const onDisconnected = (r, err) => {
    push("roomDisconnected", {
      code: err && err.code,
      message: err && err.message,
    });
    flush();
  };

  room.on("participantConnected", onParticipantConnected);
  room.on("participantDisconnected", onParticipantDisconnected);
  room.on("reconnecting", onReconnecting);
  room.on("reconnected", onReconnected);
  room.on("dominantSpeakerChanged", onDominantSpeakerChanged);
  room.on("disconnected", onDisconnected);

  // Local participant track publication events
  const lp = room.localParticipant;
  const onLocalTrackPublished = (pub) => {
    push("localTrackPublished", {
      kind: pub.kind,
      trackSid: pub.trackSid,
    });
  };
  const onLocalTrackPublicationFailed = (err, track) => {
    push("localTrackPublicationFailed", {
      kind: track && track.kind,
      code: err && err.code,
      message: err && err.message,
    });
  };
  const onLocalTrackStarted = (track) => {
    push("localTrackStarted", { kind: track.kind, sid: track.sid });
  };
  const onLocalTrackStopped = (track) => {
    push("localTrackStopped", { kind: track.kind, sid: track.sid });
  };
  if (lp) {
    lp.on("trackPublished", onLocalTrackPublished);
    lp.on("trackPublicationFailed", onLocalTrackPublicationFailed);
    lp.on("trackStarted", onLocalTrackStarted);
    lp.on("trackStopped", onLocalTrackStopped);
  }

  // Periodic getStats() poll — bytes flowing = media working. Zero bytes
  // in/out after 15 s = the classic "signalling OK, media dead" scenario.
  const pollStats = async () => {
    try {
      const statsArray = await room.getStats();
      // Twilio returns an array of StatsReport, one per PeerConnection.
      const snapshot = statsArray.map((s) => ({
        peerConnectionId: s.peerConnectionId,
        localAudioTrackStats: (s.localAudioTrackStats || []).map((t) => ({
          bytesSent: t.bytesSent,
          packetsSent: t.packetsSent,
          packetsLost: t.packetsLost,
          codec: t.codec,
          roundTripTime: t.roundTripTime,
        })),
        localVideoTrackStats: (s.localVideoTrackStats || []).map((t) => ({
          bytesSent: t.bytesSent,
          packetsSent: t.packetsSent,
          packetsLost: t.packetsLost,
          codec: t.codec,
          roundTripTime: t.roundTripTime,
          dimensions: t.dimensions,
        })),
        remoteAudioTrackStats: (s.remoteAudioTrackStats || []).map((t) => ({
          trackSid: t.trackSid,
          bytesReceived: t.bytesReceived,
          packetsReceived: t.packetsReceived,
          packetsLost: t.packetsLost,
          codec: t.codec,
          jitter: t.jitter,
        })),
        remoteVideoTrackStats: (s.remoteVideoTrackStats || []).map((t) => ({
          trackSid: t.trackSid,
          bytesReceived: t.bytesReceived,
          packetsReceived: t.packetsReceived,
          packetsLost: t.packetsLost,
          codec: t.codec,
          dimensions: t.dimensions,
        })),
      }));
      push("stats", { snapshot });
    } catch (err) {
      push("statsError", { message: err && err.message });
    }
    // ICE state is near-static once negotiated. Push only on change, so a
    // 30-minute call contributes two or three entries instead of ~360 and
    // cannot crowd out other events under MAX_EVENTS.
    const ice = await collectIceInfo(room);
    const iceSignature = ice ? JSON.stringify(ice) : "unavailable";
    if (iceSignature !== lastIceSignature) {
      lastIceSignature = iceSignature;
      if (ice) {
        // One event per peer connection. Keeps every logged line well inside
        // the backend's 512-char cap no matter how many connections a room
        // negotiates — a single combined event would truncate at two.
        ice.forEach((pc, idx) => push("ice", Object.assign({ i: idx }, pc)));
      } else {
        push("iceUnavailable", {});
      }
    }
  };
  const statsTimer = setInterval(pollStats, STATS_POLL_INTERVAL_MS);

  // Ensure we don't lose the log if the tab closes.
  const onPageHide = () => flush();
  window.addEventListener("pagehide", onPageHide);

  function flush() {
    devLog("[VIDEOCALL-DIAG] flush called", {
      alreadyFlushed: flushed,
      eventsCount: events.length,
    });
    if (flushed) return;
    flushed = true;
    clearInterval(statsTimer);

    // Detach handlers
    remoteParticipantHandlers.forEach(({ participant, handlers }) => {
      Object.entries(handlers).forEach(([evt, fn]) => {
        try {
          participant.removeListener(evt, fn);
        } catch (_) {}
      });
    });
    remoteParticipantHandlers.clear();
    try {
      room.removeListener("participantConnected", onParticipantConnected);
      room.removeListener("participantDisconnected", onParticipantDisconnected);
      room.removeListener("reconnecting", onReconnecting);
      room.removeListener("reconnected", onReconnected);
      room.removeListener("dominantSpeakerChanged", onDominantSpeakerChanged);
      room.removeListener("disconnected", onDisconnected);
      if (lp) {
        lp.removeListener("trackPublished", onLocalTrackPublished);
        lp.removeListener(
          "trackPublicationFailed",
          onLocalTrackPublicationFailed
        );
        lp.removeListener("trackStarted", onLocalTrackStarted);
        lp.removeListener("trackStopped", onLocalTrackStopped);
      }
    } catch (_) {}
    window.removeEventListener("pagehide", onPageHide);

    const payload = {
      roomName: meta && meta.roomName ? meta.roomName : room.name,
      roomSid: room.sid,
      role: meta && meta.role,
      userId: meta && meta.userId,
      facilityId: meta && meta.facilityId,
      userAgent:
        typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
      startedAt,
      endedAt: Date.now(),
      durationMs: Date.now() - startedAt,
      events,
    };

    const url = diagnosticsUrl();
    devLog("[VIDEOCALL-DIAG] posting", {
      url,
      payloadSize: JSON.stringify(payload).length,
    });
    try {
      // keepalive lets the POST survive tab unload
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        keepalive: true,
        body: JSON.stringify(payload),
      })
        .then((r) =>
          devLog("[VIDEOCALL-DIAG] POST response", r.status, r.statusText)
        )
        .catch((err) => devLog("[VIDEOCALL-DIAG] POST failed", err));
    } catch (err) {
      devLog("[VIDEOCALL-DIAG] flush threw", err);
    }
  }

  return { flush };
}
